import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchOldAllMember } from "../Redux/Slice/AddMember";
import Loader from "../Components/Loader/Loader";

const InputFieldWithLabel = ({
  label,
  name,
  type = "text",
  placeholder,
  value,
  error,
  onChange,
}) => (
  <div className="col-span-12 md:col-span-6">
    <label className="block text-sm font-bold text-gray-900 mb-1">
      {label}
    </label>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47]"
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const InternshipOfferLetter = () => {
  const dispatch = useDispatch();
  const { MemberOldAllData, MemberOldAllLoading } = useSelector(
    (state) => state.AddMemberSlice
  );

  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    internName: "",
    internshipPeriod: "",
    internshipStartDate: "",
    internshipEndDate: "",
    companyName: "",
    position: "",
    acceptanceDeadline: "",
    workingHours: "Monday to Saturday, 10:00 AM to 7:00 PM",
  });

  const [errors, setErrors] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const pdfContentRef = useRef(null);

  // Fetch employees on mount
  useEffect(() => {
    dispatch(fetchOldAllMember());
  }, [dispatch]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const calculateEndDate = (startDateStr, monthsStr) => {
    if (!startDateStr || !monthsStr) return "";
    const date = new Date(startDateStr);
    if (isNaN(date.getTime())) return "";
    const months = parseInt(monthsStr);
    if (isNaN(months)) return "";
    date.setMonth(date.getMonth() + months - 1);
    // Set to the last day of that month
    const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    nextMonth.setDate(nextMonth.getDate() - 1);
    const year = nextMonth.getFullYear();
    const month = String(nextMonth.getMonth() + 1).padStart(2, "0");
    const day = String(nextMonth.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const calculateAcceptanceDeadline = (startDateStr) => {
    if (!startDateStr) return "";
    const date = new Date(startDateStr);
    if (isNaN(date.getTime())) return "";
    date.setDate(date.getDate() + 2);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Populate data when employee is selected
  const handleEmployeeSelect = (e) => {
    const empId = e.target.value;
    setSelectedEmpId(empId);
    setGeneratedText("");

    if (!empId) {
      resetForm();
      return;
    }

    const emp = MemberOldAllData?.data?.find((el) => el._id === empId);
    if (emp) {
      const startDate = formatDateForInput(emp.JoiningDate);
      const period = emp.internshiptime || "";
      const calculatedEnd = calculateEndDate(startDate, period);
      const calculatedAcceptance = calculateAcceptanceDeadline(startDate);

      setFormValues({
        internName: emp.Name || "",
        internshipPeriod: period,
        internshipStartDate: startDate,
        internshipEndDate: calculatedEnd,
        companyName: emp.Firm || "",
        position: emp.Role || "",
        acceptanceDeadline: calculatedAcceptance,
        workingHours: "Monday to Saturday, 10:00 AM to 7:00 PM",
      });
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormValues((prev) => {
      const updated = { ...prev, [name]: value };
      // Auto calculate end date if start date or period changes
      if (name === "internshipStartDate" || name === "internshipPeriod") {
        updated.internshipEndDate = calculateEndDate(
          updated.internshipStartDate,
          updated.internshipPeriod
        );
      }
      // Auto calculate acceptance deadline if start date changes
      if (name === "internshipStartDate") {
        updated.acceptanceDeadline = calculateAcceptanceDeadline(value);
      }
      return updated;
    });

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[name]) delete newErrors[name];
      return newErrors;
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formValues.internName)
      newErrors.internName = "Intern Name is required";
    if (!formValues.companyName)
      newErrors.companyName = "Company Name is required";
    if (!formValues.position) newErrors.position = "Position is required";
    if (!formValues.internshipStartDate)
      newErrors.internshipStartDate = "Internship Start Date is required";
    if (!formValues.internshipEndDate)
      newErrors.internshipEndDate = "Internship End Date is required";
    if (!formValues.internshipPeriod || isNaN(formValues.internshipPeriod))
      newErrors.internshipPeriod =
        "Valid Internship Period (months) is required";
    if (!formValues.acceptanceDeadline)
      newErrors.acceptanceDeadline = "Acceptance Deadline is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateTemplateText = (data) => {
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    const formattedStartDate = formatDate(data.internshipStartDate);
    const formattedEndDate = formatDate(data.internshipEndDate);
    const formattedAcceptanceDate = formatDate(data.acceptanceDeadline);

    return `
<div style="font-family: Arial, sans-serif; line-height: 1rem; font-size: 13px;">
  <h3 style=" margin-bottom: 80px;"></h3>

  <p style=" margin-bottom: 0px;">Dear <strong>${data.internName}</strong>,</p>
  <p style="text-align: justify;">
    We are pleased to offer you the position of <strong>${data.position}</strong> Intern at <strong>${data.companyName}</strong>.This internship is for a period of <strong>${data.internshipPeriod} months</strong>, starting from <strong>${formattedStartDate}</strong> and concluding on <strong>${formattedEndDate}</strong>.
    Below are the terms and details of the offer:
  </p>

  <p><strong>Position -</strong> <strong>${data.position}</strong></p>
  <p><strong>Duration -</strong> ${data.internshipPeriod} months (from <strong>${formattedStartDate} to ${formattedEndDate}</strong>)</p>
  <p style=" margin-bottom:0px;"><strong>Work Days and Hours -</strong> ${data.workingHours}</p>
  <p>
    We look forward to welcoming you to our team and hope that your time with us will be both
productive and enjoyable. Please sign and return a copy of this letter by <strong>${formattedAcceptanceDate}</strong> to confirm
your acceptance of this offer.
  </p>

  <p style="margin-top: 20px;">Sincerely,</p>
  <p style="margin-top: 20px;">Dipak Rajput<br /></p>
  <p>Project Manager</p>

  <p style="font-weight: bold;">Acceptance of Offer</p>
  <p>
    I, <strong>${data.internName}</strong>, accept the terms and conditions of this internship offer.
  </p>

  <p>
    Signature: _____________<br />
    Date: _____________
  </p>
</div>
  `;
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const text = generateTemplateText(formValues);
      setGeneratedText(text);
      setErrors({});
      setIsModalOpen(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      internName: "",
      internshipPeriod: "",
      internshipStartDate: "",
      internshipEndDate: "",
      companyName: "",
      position: "",
      acceptanceDeadline: "",
      workingHours: "Monday to Saturday, 10:00 AM to 7:00 PM",
    });
    setErrors({});
    setGeneratedText("");
    setSelectedEmpId("");
  };

  const handleDownloadPdf = async () => {
    const element = pdfContentRef.current;
    if (!element) return;

    const pdf = new jsPDF("p", "mm", "a4");
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Internship_Offer_${formValues.internName || "Intern"}.pdf`);
  };

  const getFormattedHtml = (text) =>
    text ? text.replace(/\n/g, "<br />") : "";

  if (MemberOldAllLoading) return <Loader />;

  return (
    <div className="min-h-screen p-4 lg:p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold bg-[#122A47] text-white px-4 py-2 inline-block rounded mb-6">
          INTERNSHIP OFFER LETTER
        </h1>

        {/* STEP 1: Employee Selection Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#122A47] mb-4">
            Step 1: Select Employee
          </h2>
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-12 md:col-span-8">
              <select
                value={selectedEmpId}
                onChange={handleEmployeeSelect}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47] bg-white text-gray-900 font-medium"
              >
                <option value="">-- Choose Employee / Intern --</option>
                {MemberOldAllData?.data?.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.Name} {emp.Role ? `(${emp.Role})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-12 md:col-span-4 flex gap-2">
              <button
                disabled={!selectedEmpId}
                onClick={() => setIsModalOpen(true)}
                className={`flex-1 text-white py-2 px-4 rounded-md transition-colors font-semibold ${
                  selectedEmpId
                    ? "bg-[#122A47] hover:bg-[#0c2545]"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Fill Details & Open Template
              </button>
              <button
                onClick={resetForm}
                className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-semibold"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* STEP 2: The Modal (Popup Form) */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-[#122A47] text-white rounded-t-lg">
                <h3 className="text-lg font-bold">Edit Internship Offer Details</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-300 text-2xl font-bold"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  {/* Database-prefilled fields */}
                  <InputFieldWithLabel
                    label="Intern's Name (from Database)"
                    name="internName"
                    value={formValues.internName}
                    error={errors.internName}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Company Name
                    </label>
                    <select
                      name="companyName"
                      value={formValues.companyName}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Company --</option>
                      <option value="Dynamic Software">Dynamic Software</option>
                      <option value="Dynamic Softlink Pvt Ltd.">Dynamic Softlink Pvt Ltd.</option>
                      {formValues.companyName &&
                        formValues.companyName !== "Dynamic Software" &&
                        formValues.companyName !== "Dynamic Softlink Pvt Ltd." && (
                          <option value={formValues.companyName}>
                            {formValues.companyName} (from DB)
                          </option>
                        )}
                    </select>
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                    )}
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Position / Designation
                    </label>
                    <select
                      name="position"
                      value={formValues.position}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Position --</option>
                      <option value="Delphi Developer">Delphi Developer</option>
                      <option value="Web Developer">Web Developer</option>
                      {formValues.position &&
                        formValues.position !== "Delphi Developer" &&
                        formValues.position !== "Web Developer" && (
                          <option value={formValues.position}>
                            {formValues.position} (from DB)
                          </option>
                        )}
                    </select>
                    {errors.position && (
                      <p className="text-red-500 text-sm mt-1">{errors.position}</p>
                    )}
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Internship Period (Months)
                    </label>
                    <select
                      name="internshipPeriod"
                      value={formValues.internshipPeriod}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Period --</option>
                      {[1, 2, 3, 4, 5, 6].map((m) => (
                        <option key={m} value={m}>
                          {m} {m === 1 ? "Month" : "Months"}
                        </option>
                      ))}
                      {formValues.internshipPeriod &&
                        ![1, 2, 3, 4, 5, 6].includes(Number(formValues.internshipPeriod)) && (
                          <option value={formValues.internshipPeriod}>
                            {formValues.internshipPeriod} Months (from DB)
                          </option>
                        )}
                    </select>
                    {errors.internshipPeriod && (
                      <p className="text-red-500 text-sm mt-1">{errors.internshipPeriod}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Internship Start Date"
                    name="internshipStartDate"
                    type="date"
                    value={formValues.internshipStartDate}
                    error={errors.internshipStartDate}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Internship End Date (Auto-calculated)"
                    name="internshipEndDate"
                    type="date"
                    value={formValues.internshipEndDate}
                    error={errors.internshipEndDate}
                    onChange={handleChange}
                  />

                  {/* Manual entry field */}
                  <InputFieldWithLabel
                    label="Acceptance Deadline (Required)"
                    name="acceptanceDeadline"
                    type="date"
                    value={formValues.acceptanceDeadline}
                    error={errors.acceptanceDeadline}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Working Days & Hours
                    </label>
                    <select
                      name="workingHours"
                      value={formValues.workingHours}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Work Hours --</option>
                      <option value="Monday to Saturday, 10:00 AM to 7:00 PM">
                        Monday to Saturday, 10:00 AM to 7:00 PM
                      </option>
                      <option value="Monday to Saturday, 10:00 AM to 6:00 PM">
                        Monday to Saturday, 10:00 AM to 6:00 PM
                      </option>
                      {formValues.workingHours &&
                        formValues.workingHours !== "Monday to Saturday, 10:00 AM to 7:00 PM" &&
                        formValues.workingHours !== "Monday to Saturday, 10:00 AM to 6:00 PM" && (
                          <option value={formValues.workingHours}>
                            {formValues.workingHours} (Custom)
                          </option>
                        )}
                    </select>
                    {errors.workingHours && (
                      <p className="text-red-500 text-sm mt-1">{errors.workingHours}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-[#122A47] text-white py-2 px-4 rounded-md hover:bg-[#0c2545] transition-colors font-medium"
                  >
                    Generate Document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: Preview and Download Panel */}
        {generatedText && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#122A47]">
                Letter Preview
              </h2>
              <button
                onClick={handleDownloadPdf}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-semibold"
              >
                Download PDF
              </button>
            </div>

            <div className="max-w-full overflow-x-auto bg-gray-100 p-4 rounded-lg border border-gray-300">
              <div
                ref={pdfContentRef}
                className="bg-white shadow-lg mx-auto"
                style={{
                  width: "180mm",
                  minHeight: "270mm",
                  padding: "15mm",
                  boxSizing: "border-box",
                }}
              >
                <div
                  className="font-sans text-[13px] text-left leading-relaxed text-gray-900"
                  dangerouslySetInnerHTML={{
                    __html: getFormattedHtml(generatedText),
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternshipOfferLetter;
